/**
 * Ngữ cảnh giao diện.
 *
 * Hai giai đoạn, và cả hai đều cần thiết:
 *
 *   1. `main.tsx` áp NGAY một giao diện đoán được từ thương hiệu đã nhớ của tên miền
 *      này, đồng bộ, trước khi React vẽ lần đầu. Không có bước này thì người mở một
 *      website nền sáng sẽ thấy một nháy nền tối trước.
 *   2. Ngữ cảnh này hỏi `GET /v1/brand` — nguồn sự thật — rồi áp lại nếu khác.
 *
 * Lần đầu vào một tên miền chưa nhớ thì bước 1 ra mặc định và bước 2 sửa lại sau vài
 * trăm mili giây. Từ lần sau thì bước 1 đã đúng ngay.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type JSX, type ReactNode,
} from 'react';
import {
  applyTheme, applyTitle, fetchBrand, rememberTheme, resolveFromCache, resolveTheme, themeById,
  type Brand, type Theme,
} from './index';

interface ThemeContextValue {
  theme: Theme;
  /** Thương hiệu của tên miền hiện tại, hoặc null nếu tên miền chưa được gắn. */
  brand: Brand | null;
  /** Chưa hỏi xong máy chủ: trang có thể đang hiện giao diện đoán. */
  dangTai: boolean;
  /** Tên miền của một nhà khai thác thật thì người xem không được đổi giao diện. */
  locked: boolean;
  setTheme: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const doan = useMemo(() => resolveFromCache(window.location), []);
  const [theme, setThemeState] = useState<Theme>(doan.theme);
  const [brand, setBrand] = useState<Brand | null>(doan.brand);
  const [dangTai, setDangTai] = useState(true);

  useEffect(() => {
    let conSong = true;
    void fetchBrand().then((tuMayChu) => {
      if (!conSong) return;
      const that = resolveTheme(window.location, tuMayChu);
      setBrand(tuMayChu);
      setThemeState(that.theme);
      applyTheme(that.theme);
      applyTitle(that.theme, tuMayChu);
      setDangTai(false);
    });
    return () => {
      conSong = false;
    };
  }, []);

  /**
   * Khóa khi tên miền thuộc một thương hiệu KHÔNG bật cờ xem thử.
   *
   * Cờ đó là dữ liệu chứ không phải suy diễn, và lý do rất cụ thể: sau khi mọi tên miền
   * đều được gắn thương hiệu (kể cả tên miền trình diễn), quy tắc cũ "khóa khi tên miền
   * có trong bảng ánh xạ" sẽ tự khóa luôn cả trang trình diễn.
   */
  const locked = brand !== null && !brand.allow_theme_preview;

  const setTheme = useCallback(
    (id: string) => {
      if (locked) return;
      const next = themeById(id);
      if (!next) return;
      applyTheme(next);
      applyTitle(next, brand);
      rememberTheme(next.id);
      setThemeState(next);
    },
    [locked, brand],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, brand, dangTai, locked, setTheme }),
    [theme, brand, dangTai, locked, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme phai nam trong <ThemeProvider>');
  return ctx;
}
